import type { CallShape } from './call-shapes';

/**
 * Quais casos de uso do container do navegador viram chamada de rede, e em que forma.
 *
 * O servidor tem a lista dele em `server/api/endpoints.ts`, e essa é a que vale: é ela
 * que decide o que existe como endpoint e sob que garantia. Este catálogo é o **espelho**
 * dela do lado de cá, e um espelho serve para conferir, não para decidir.
 *
 * ## Por que não importar a lista do servidor e pronto
 *
 * Porque o `registry` de lá resolve o caminho a partir do **nome da classe**, lido em
 * tempo de execução (`candidate.constructor.name`). No servidor isso funciona; num pacote
 * de navegador, não se pode contar com ele — o minificador renomeia classes, e o dia em
 * que renomear, todas as rotas viram `/api/n` de uma vez, em produção e não no teste.
 *
 * Então o nome da classe é **escrito** aqui, como texto, e o
 * `endpoint-catalog.test.ts` confere que ele continua batendo com a lista do servidor:
 * nenhum endpoint sem entrada, nenhuma entrada sem endpoint, nenhuma forma divergente.
 *
 * ## Dez que saíram, e o motivo de cada família
 *
 * Estavam aqui e foram removidos: as telas os chamavam e recebiam **404**, porque o
 * servidor não os monta. Ficaram no navegador até a Fase 32, que tirou do navegador o banco
 * sobre o qual eles operavam — e então saíram de vez.
 *
 * - **Arquivamento de anexo** (`getArchivableAttachments`, `archiveOldAttachments`) — eram
 *   do expurgo do navegador, e o servidor guarda o arquivo enquanto o registro existir;
 * - **Armazenamento** (`getStorageBreakdown`, `getPurgeableImports`, `purgeImportRows`) —
 *   dependiam do `StorageMaintenanceRepository`, que só existia sobre IndexedDB;
 * - **Entrada por e-mail** (`requestLoginLink`, `consumeLoginLink`, `inviteUser`) — o
 *   remetente saiu do sistema junto com o primeiro acesso: num sistema fechado, quem dá a
 *   primeira senha é quem administra.
 *
 * ## O que **não** está aqui
 *
 * O que é do aparelho — o token da sessão, a obra aberta, a cota, as permissões e o relógio
 * — e o servidor nem monta (`createDeviceContainer`, em `src/app/container.ts`, diz de cada
 * um por quê). Não vira rede; continua falando com o navegador.
 *
 * **O anexo saiu dessa lista na Fase 22.** A `0027` deu ao binário um lugar no servidor
 * (`attachment_blobs`), e sem isso a foto tirada no tablet ficava no tablet: o relatório
 * fotográfico do RDO não saía de outro aparelho. Ele sobe em base64 pelo `withBytes`, que
 * já era o caminho das planilhas, e volta como `data:` — que a tag `<img>` desenha igual.
 *
 * `ListUsers` era o décimo oitavo, e o preço de ele ter ficado de fora apareceu na tela
 * de perfis: o cadastro ia para o servidor e a lista o procurava no banco local, que
 * estava vazio. Perfil criado não aparecia, o segundo cadastro batia em "e-mail já
 * cadastrado", e ninguém conseguia definir a senha de quem só existia do outro lado.
 * Ganhou permissão (`users.manage` ou `roles.manage`) e voltou para cá.
 */

export interface RemoteEndpoint {
  /** Nome da classe, que é o que vira o caminho: `GetProject` -> `/api/get-project`. */
  readonly name: string;
  readonly shape: CallShape;
  /**
   * O caso de uso devolve o valor **cru**, e não um `Result`.
   *
   * Cinco dos cento e seis fazem isso, e todos pelo mesmo motivo: são catálogos, e não há
   * falha esperada em listar papéis ou unidades. O servidor aceita os dois formatos — o
   * `toResult` de `bindings.ts` embrulha o que vier — e é justamente por isso que a
   * assimetria some de vista: do outro lado da rede **tudo** volta embrulhado.
   *
   * Sem esta bandeira, a tela receberia `{ ok: true, value: [...] }` onde espera a lista,
   * e o que apareceria seria `allProjects.filter is not a function` — longe daqui, dentro
   * de um componente que não fez nada de errado.
   */
  readonly raw?: true;
}

export const REMOTE_ENDPOINTS = {
  getMySession: { name: 'GetMySession', shape: { kind: 'withSessionFirst' } },
  listSupplyProjects: { name: 'ListSupplyProjects', shape: { kind: 'withSessionFirst' } },
  searchSupplyProducts: {
    name: 'SearchSupplyProducts',
    shape: { kind: 'withSessionFirst', extra: 'term' },
  },
  // O preço médio do compras (Fase 29, RN-394).
  getSupplyAveragePrices: { name: 'GetSupplyAveragePrices', shape: { kind: 'withInput' } },
  // O preço do material na obra (Fase 29, etapa 2 — RN-397).
  getProjectMaterialPrices: { name: 'GetProjectMaterialPrices', shape: { kind: 'withInput' } },
  setMaterialPrice: { name: 'SetMaterialPrice', shape: { kind: 'withInput' } },
  refreshMaterialPrices: { name: 'RefreshMaterialPrices', shape: { kind: 'withInput' } },
  // O fator de unidade do produto, para todas as obras (13/10/2026).
  getProductUnitFactors: { name: 'GetProductUnitFactors', shape: { kind: 'withInput' } },
  setProductUnitFactors: { name: 'SetProductUnitFactors', shape: { kind: 'withInput' } },
  // O custo da mão de obra no orçamento (Fase 29, etapa 3 — RN-399).
  getProjectLaborRates: { name: 'GetProjectLaborRates', shape: { kind: 'withInput' } },
  setLaborProvisionalRate: { name: 'SetLaborProvisionalRate', shape: { kind: 'withInput' } },
  // O orçamento aprovado da obra (Fase 29, etapa 4 — D16, RN-400).
  approveProjectBudget: { name: 'ApproveProjectBudget', shape: { kind: 'withInput' } },
  getProjectApprovedBudget: { name: 'GetProjectApprovedBudget', shape: { kind: 'withInput' } },
  listUsers: { name: 'ListUsers', shape: { kind: 'withInput' } },
  createUser: { name: 'CreateUser', shape: { kind: 'withInput' } },
  updateUser: { name: 'UpdateUser', shape: { kind: 'withInput' } },
  updateMyProfile: { name: 'UpdateMyProfile', shape: { kind: 'withInput' } },
  assignRole: { name: 'AssignRole', shape: { kind: 'withInput' } },
  revokeRole: { name: 'RevokeRole', shape: { kind: 'withInput' } },
  transferSuperAdmin: { name: 'TransferSuperAdmin', shape: { kind: 'withInput' } },
  createRole: { name: 'CreateRole', shape: { kind: 'withInput' } },
  updateRole: { name: 'UpdateRole', shape: { kind: 'withInput' } },
  deleteRole: { name: 'DeleteRole', shape: { kind: 'withInput' } },
  listRoles: { name: 'ListRoles', shape: { kind: 'withNothing' }, raw: true },
  listPermissions: { name: 'ListPermissions', shape: { kind: 'withNothing' }, raw: true },
  listAccessibleProjects: {
    name: 'ListAccessibleProjects',
    shape: { kind: 'withSessionFirst' },
    raw: true,
  },
  getDefaultWorkCalendar: {
    name: 'GetDefaultWorkCalendar',
    shape: { kind: 'withNothing' },
    raw: true,
  },
  listRecentAudit: { name: 'ListRecentAudit', shape: { kind: 'withSessionFirst', extra: 'limit' } },
  createProject: { name: 'CreateProject', shape: { kind: 'withInput' } },
  updateProject: { name: 'UpdateProject', shape: { kind: 'withInput' } },
  // As versões de orçamento da obra (07/10/2026).
  duplicateProject: { name: 'DuplicateProject', shape: { kind: 'withInput' } },
  deleteProjectVersion: { name: 'DeleteProjectVersion', shape: { kind: 'withInput' } },
  approveProjectVersion: { name: 'ApproveProjectVersion', shape: { kind: 'withInput' } },
  previewProjectVersionApproval: {
    name: 'PreviewProjectVersionApproval',
    shape: { kind: 'withInput' },
  },
  transferServicesToApproved: {
    name: 'TransferServicesToApproved',
    shape: { kind: 'withInput' },
  },
  getProject: { name: 'GetProject', shape: { kind: 'withField', name: 'projectId' } },
  getProjectStructure: {
    name: 'GetProjectStructure',
    shape: { kind: 'withField', name: 'projectId' },
  },
  // A estrutura sem as linhas de material, para as caixas de filtro (Fase 26).
  getProjectOutline: { name: 'GetProjectOutline', shape: { kind: 'withInput' } },
  createTower: { name: 'CreateTower', shape: { kind: 'withInput' } },
  renameTower: { name: 'RenameTower', shape: { kind: 'withInput' } },
  createFloor: { name: 'CreateFloor', shape: { kind: 'withInput' } },
  createFloorRange: { name: 'CreateFloorRange', shape: { kind: 'withInput' } },
  renameFloor: { name: 'RenameFloor', shape: { kind: 'withInput' } },
  createDiscipline: { name: 'CreateDiscipline', shape: { kind: 'withInput' } },
  listDisciplineCatalog: { name: 'ListDisciplineCatalog', shape: { kind: 'withSessionFirst' } },
  updateDiscipline: { name: 'UpdateDiscipline', shape: { kind: 'withInput' } },
  setDisciplineActive: { name: 'SetDisciplineActive', shape: { kind: 'withInput' } },
  previewDisciplineImport: { name: 'PreviewDisciplineImport', shape: { kind: 'withBytes' } },
  confirmDisciplineImport: { name: 'ConfirmDisciplineImport', shape: { kind: 'withInput' } },
  createActivity: { name: 'CreateActivity', shape: { kind: 'withInput' } },
  applyDisciplinesToFloors: { name: 'ApplyDisciplinesToFloors', shape: { kind: 'withInput' } },
  changeActivityStatus: { name: 'ChangeActivityStatus', shape: { kind: 'withInput' } },
  listUnitsOfMeasure: { name: 'ListUnitsOfMeasure', shape: { kind: 'withNothing' }, raw: true },
  updateActivitySchedule: { name: 'UpdateActivitySchedule', shape: { kind: 'withInput' } },
  deleteStructureNode: { name: 'DeleteStructureNode', shape: { kind: 'withInput' } },
  previewStructureNodeImpact: {
    name: 'PreviewStructureNodeImpact',
    shape: { kind: 'withInput' },
  },

  // Quantitativo — Fase 14. `GetProjectTakeoff` recebe a obra solta, como a leitura da
  // estrutura; o resto segue a forma padrão `execute(input, session)`.
  getProjectTakeoff: {
    name: 'GetProjectTakeoff',
    shape: { kind: 'withField', name: 'projectId' },
  },
  setUnitTypeServices: { name: 'SetUnitTypeServices', shape: { kind: 'withInput' } },
  createFloorPlan: { name: 'CreateFloorPlan', shape: { kind: 'withInput' } },
  renameFloorPlan: { name: 'RenameFloorPlan', shape: { kind: 'withInput' } },
  deleteFloorPlan: { name: 'DeleteFloorPlan', shape: { kind: 'withInput' } },
  createUnitType: { name: 'CreateUnitType', shape: { kind: 'withInput' } },
  renameUnitType: { name: 'RenameUnitType', shape: { kind: 'withInput' } },
  deleteUnitType: { name: 'DeleteUnitType', shape: { kind: 'withInput' } },
  linkFloorToFloorPlan: { name: 'LinkFloorToFloorPlan', shape: { kind: 'withInput' } },
  // O desenho: uma leitura pela obra solta e uma gravação que leva a montagem inteira
  // num envelope (Fase 14, fatia 3).
  getProjectGeometry: {
    name: 'GetProjectGeometry',
    shape: { kind: 'withField', name: 'projectId' },
  },
  saveProjectGeometry: { name: 'SaveProjectGeometry', shape: { kind: 'withInput' } },
  // O apontamento por peça (Fase 27, etapa 6): as peças da frente para o lançamento do dia,
  // e as marcas do dia, que substituem as desta data para a frente × serviço.
  getFrontPieces: { name: 'GetFrontPieces', shape: { kind: 'withInput' } },
  getImportedPieces: { name: 'GetImportedPieces', shape: { kind: 'withInput' } },
  setImportedPiecePosition: { name: 'SetImportedPiecePosition', shape: { kind: 'withInput' } },
  renameImportedPiece: { name: 'RenameImportedPiece', shape: { kind: 'withInput' } },
  clearImportedPiecePosition: { name: 'ClearImportedPiecePosition', shape: { kind: 'withInput' } },
  recordPieceExecutions: { name: 'RecordPieceExecutions', shape: { kind: 'withInput' } },
  // O avanço por cômodo (Fase 34, etapa 1): os aptos × cômodos da frente para o serviço, o
  // percentual do dia e o "não se aplica" do cômodo da tipologia.
  getFrontRooms: { name: 'GetFrontRooms', shape: { kind: 'withInput' } },
  recordRoomProgress: { name: 'RecordRoomProgress', shape: { kind: 'withInput' } },
  setRoomNotApplicable: { name: 'SetRoomNotApplicable', shape: { kind: 'withInput' } },
  // A prancha do desenho (Fase 24): o registro pela obra solta, o arquivo à parte — é o
  // único pedido que custa megabytes —, e o anexo com os bytes em base64.
  getProjectFloorPlanSheets: {
    name: 'GetProjectFloorPlanSheets',
    shape: { kind: 'withField', name: 'projectId' },
  },
  getFloorPlanSheetFile: { name: 'GetFloorPlanSheetFile', shape: { kind: 'withInput' } },
  attachFloorPlanSheet: { name: 'AttachFloorPlanSheet', shape: { kind: 'withBytes' } },
  calibrateFloorPlanSheet: { name: 'CalibrateFloorPlanSheet', shape: { kind: 'withInput' } },
  removeFloorPlanSheet: { name: 'RemoveFloorPlanSheet', shape: { kind: 'withInput' } },
  // O cadastro de serviços: a leitura pela obra solta, a gravação num envelope.
  getProjectServices: {
    name: 'GetProjectServices',
    shape: { kind: 'withField', name: 'projectId' },
  },
  applyServiceCatalog: { name: 'ApplyServiceCatalog', shape: { kind: 'withInput' } },
  // Os itens do serviço (Fase 24, fatia 3): a mesma forma da metade de cima — leitura
  // pela obra solta, gravação num envelope.
  getProjectServiceItems: {
    name: 'GetProjectServiceItems',
    shape: { kind: 'withField', name: 'projectId' },
  },
  saveProjectServiceItems: { name: 'SaveProjectServiceItems', shape: { kind: 'withInput' } },
  // A unidade do serviço, trocada no quadro Serviços × traçado (Fase 24, fatia 5): fora do
  // envelope, porque a troca pode ser recusada e a recusa volta para quem trocou.
  setProjectServiceUnit: { name: 'SetProjectServiceUnit', shape: { kind: 'withInput' } },
  // A sugestão de produtividade da mão de obra (Fase 24, fatia 7): leitura com entrada — obra,
  // serviço e função —, e por isso o formato padrão, e não a obra solta.
  getProductivitySuggestion: { name: 'GetProductivitySuggestion', shape: { kind: 'withInput' } },
  // A taxa de conversão média (Fase 39, etapa 1): a leitura é da empresa, sem entrada; as
  // duas marcas da obra vão no formato padrão.
  getRateAverages: { name: 'GetRateAverages', shape: { kind: 'withSessionFirst' } },
  // A média de um diâmetro, para o tipo de estaca da obra (etapa 2).
  getPileTypeAverage: { name: 'GetPileTypeAverage', shape: { kind: 'withInput' } },
  // A média do apartamento para a obra e a foto da medida que a bancada grava (Fase 40).
  getApartmentAverage: { name: 'GetApartmentAverage', shape: { kind: 'withInput' } },
  saveTakeoffMeasures: { name: 'SaveTakeoffMeasures', shape: { kind: 'withInput' } },
  setProjectInRateAverage: { name: 'SetProjectInRateAverage', shape: { kind: 'withInput' } },
  setCatalogReferenceProject: { name: 'SetCatalogReferenceProject', shape: { kind: 'withInput' } },
  // O cadastro global do serviço — Fase 21. A leitura não tem entrada além de quem
  // pergunta: o catálogo é um só, e não existe "o catálogo desta obra".
  listGlobalServices: { name: 'ListGlobalServices', shape: { kind: 'withSessionFirst' } },
  createGlobalService: { name: 'CreateGlobalService', shape: { kind: 'withInput' } },
  updateGlobalService: { name: 'UpdateGlobalService', shape: { kind: 'withInput' } },
  setGlobalServiceActive: { name: 'SetGlobalServiceActive', shape: { kind: 'withInput' } },
  // O catálogo de checklists de qualidade — Fase 19. Todos na forma padrão.
  listServiceChecklists: { name: 'ListServiceChecklists', shape: { kind: 'withInput' } },
  saveServiceChecklist: { name: 'SaveServiceChecklist', shape: { kind: 'withInput' } },
  setServiceChecklistActive: { name: 'SetServiceChecklistActive', shape: { kind: 'withInput' } },
  getDayQuality: { name: 'GetDayQuality', shape: { kind: 'withInput' } },
  listQualitySheetServices: { name: 'ListQualitySheetServices', shape: { kind: 'withInput' } },
  getServiceQualitySheet: { name: 'GetServiceQualitySheet', shape: { kind: 'withInput' } },
  // A aba Não conformidades (Fase 37, RN-481 a RN-484).
  listProjectNonConformities: {
    name: 'ListProjectNonConformities',
    shape: { kind: 'withInput' },
  },
  resolveNonConformity: { name: 'ResolveNonConformity', shape: { kind: 'withInput' } },
  cancelNonConformity: { name: 'CancelNonConformity', shape: { kind: 'withInput' } },
  getCatalog: { name: 'GetCatalog', shape: { kind: 'withSessionFirst' } },
  listMaterials: { name: 'ListMaterials', shape: { kind: 'withSessionFirst' } },
  createMaterial: { name: 'CreateMaterial', shape: { kind: 'withInput' } },
  // O vínculo do código provisório do orçamento ao SKU de compras (Fase 26, RN-347).
  linkMaterialToSku: { name: 'LinkMaterialToSku', shape: { kind: 'withInput' } },
  previewMaterialImport: { name: 'PreviewMaterialImport', shape: { kind: 'withBytes' } },
  confirmMaterialImport: { name: 'ConfirmMaterialImport', shape: { kind: 'withInput' } },
  startScheduleImport: { name: 'StartScheduleImport', shape: { kind: 'withBytes' } },
  validateScheduleImport: { name: 'ValidateScheduleImport', shape: { kind: 'withInput' } },
  confirmScheduleImport: {
    name: 'ConfirmScheduleImport',
    shape: { kind: 'withField', name: 'importId' },
  },
  cancelScheduleImport: {
    name: 'CancelScheduleImport',
    shape: { kind: 'withField', name: 'importId' },
  },
  listScheduleImports: {
    name: 'ListScheduleImports',
    shape: { kind: 'withField', name: 'projectId' },
  },
  getMaterialAlerts: { name: 'GetMaterialAlerts', shape: { kind: 'withField', name: 'projectId' } },
  // O cronograma por item (Fase 25). Todos na forma padrão: a leitura tem o filtro de torre
  // ao lado da obra, e a importação sobe as linhas já normalizadas pelo navegador (ADR-161),
  // em JSON, e não o arquivo.
  listScheduleItems: { name: 'ListScheduleItems', shape: { kind: 'withInput' } },
  previewScheduleItemsImport: {
    name: 'PreviewScheduleItemsImport',
    shape: { kind: 'withInput' },
  },
  confirmScheduleItemsImport: {
    name: 'ConfirmScheduleItemsImport',
    shape: { kind: 'withInput' },
  },
  updateScheduleItem: { name: 'UpdateScheduleItem', shape: { kind: 'withInput' } },
  // O cronograma a partir do quantitativo (Fase 31). Forma padrão: o quantitativo por tipologia
  // sobe calculado pela tela, em JSON, ao lado da obra — o servidor não refaz a conta do desenho.
  previewScheduleFromTakeoff: {
    name: 'PreviewScheduleFromTakeoff',
    shape: { kind: 'withInput' },
  },
  confirmScheduleFromTakeoff: {
    name: 'ConfirmScheduleFromTakeoff',
    shape: { kind: 'withInput' },
  },
  getScheduleTakeoffDrift: { name: 'GetScheduleTakeoffDrift', shape: { kind: 'withInput' } },
  previewScheduleTakeoffRefresh: {
    name: 'PreviewScheduleTakeoffRefresh',
    shape: { kind: 'withInput' },
  },
  applyScheduleTakeoffRefresh: {
    name: 'ApplyScheduleTakeoffRefresh',
    shape: { kind: 'withInput' },
  },
  // Os itens de compra e o HH por função do item (Fase 26). Forma padrão: a lista leva os
  // filtros e a página ao lado da obra, e as importações sobem as linhas já normalizadas
  // pelo navegador (ADR-170), em JSON, e não o arquivo.
  listPurchaseLines: { name: 'ListPurchaseLines', shape: { kind: 'withInput' } },
  previewPurchaseLinesImport: {
    name: 'PreviewPurchaseLinesImport',
    shape: { kind: 'withInput' },
  },
  confirmPurchaseLinesImport: {
    name: 'ConfirmPurchaseLinesImport',
    shape: { kind: 'withInput' },
  },
  // O cronograma por item a partir do quantitativo (Fase 35): o quantitativo por tipologia ×
  // serviço × item vai em JSON, calculado pela tela, como na geração da Fase 31.
  previewPurchaseLinesFromTakeoff: {
    name: 'PreviewPurchaseLinesFromTakeoff',
    shape: { kind: 'withInput' },
  },
  confirmPurchaseLinesFromTakeoff: {
    name: 'ConfirmPurchaseLinesFromTakeoff',
    shape: { kind: 'withInput' },
  },
  previewLaborImport: { name: 'PreviewLaborImport', shape: { kind: 'withInput' } },
  confirmLaborImport: { name: 'ConfirmLaborImport', shape: { kind: 'withInput' } },
  listItemLabor: { name: 'ListItemLabor', shape: { kind: 'withInput' } },
  getDashboard: { name: 'GetDashboard', shape: { kind: 'withInput' } },
  getWorkforce: { name: 'GetWorkforce', shape: { kind: 'withField', name: 'projectId' } },
  createJobRole: { name: 'CreateJobRole', shape: { kind: 'withInput' } },
  setJobRoleDisciplines: { name: 'SetJobRoleDisciplines', shape: { kind: 'withInput' } },
  previewEmployeeImport: { name: 'PreviewEmployeeImport', shape: { kind: 'withBytes' } },
  confirmEmployeeImport: { name: 'ConfirmEmployeeImport', shape: { kind: 'withInput' } },
  previewStructureImport: { name: 'PreviewStructureImport', shape: { kind: 'withBytes' } },
  confirmStructureImport: { name: 'ConfirmStructureImport', shape: { kind: 'withInput' } },
  createEmployee: { name: 'CreateEmployee', shape: { kind: 'withInput' } },
  updateEmployee: { name: 'UpdateEmployee', shape: { kind: 'withInput' } },
  changeEmployeeStatus: { name: 'ChangeEmployeeStatus', shape: { kind: 'withInput' } },
  createTeam: { name: 'CreateTeam', shape: { kind: 'withInput' } },
  moveEmployeeToTeam: { name: 'MoveEmployeeToTeam', shape: { kind: 'withInput' } },
  createExternalService: { name: 'CreateExternalService', shape: { kind: 'withInput' } },
  updateExternalService: { name: 'UpdateExternalService', shape: { kind: 'withInput' } },
  updateTeam: { name: 'UpdateTeam', shape: { kind: 'withInput' } },
  // As disciplinas que a equipe executa (Fase 30, RN-433): a lista inteira; vazia volta à maioria.
  setTeamDisciplines: { name: 'SetTeamDisciplines', shape: { kind: 'withInput' } },
  getWorkCalendar: { name: 'GetWorkCalendar', shape: { kind: 'withField', name: 'projectId' } },
  getSystemCalendar: { name: 'GetSystemCalendar', shape: { kind: 'withSessionFirst' } },
  addCalendarException: { name: 'AddCalendarException', shape: { kind: 'withInput' } },
  importCalendarExceptions: {
    name: 'ImportCalendarExceptions',
    shape: { kind: 'withInput' },
  },
  removeCalendarException: { name: 'RemoveCalendarException', shape: { kind: 'withInput' } },
  getResourceCapacity: {
    name: 'GetResourceCapacity',
    shape: { kind: 'withFields', first: 'employeeId', second: 'date' },
  },
  addActivityItem: { name: 'AddActivityItem', shape: { kind: 'withInput' } },
  updateActivityItem: { name: 'UpdateActivityItem', shape: { kind: 'withInput' } },
  getWeeklyBoard: { name: 'GetWeeklyBoard', shape: { kind: 'withInput' } },
  getResourceAvailability: { name: 'GetResourceAvailability', shape: { kind: 'withInput' } },
  suggestWeeklyDistribution: { name: 'SuggestWeeklyDistribution', shape: { kind: 'withInput' } },
  getOrCreateWeeklyPlan: { name: 'GetOrCreateWeeklyPlan', shape: { kind: 'withInput' } },
  createWeeklyAllocation: { name: 'CreateWeeklyAllocation', shape: { kind: 'withInput' } },
  updateWeeklyAllocation: { name: 'UpdateWeeklyAllocation', shape: { kind: 'withInput' } },
  deleteWeeklyAllocation: { name: 'DeleteWeeklyAllocation', shape: { kind: 'withInput' } },
  changeWeeklyPlanStatus: { name: 'ChangeWeeklyPlanStatus', shape: { kind: 'withInput' } },
  suggestScheduleAllocation: { name: 'SuggestScheduleAllocation', shape: { kind: 'withInput' } },
  applyScheduleAllocation: { name: 'ApplyScheduleAllocation', shape: { kind: 'withInput' } },
  getDailySchedule: { name: 'GetDailySchedule', shape: { kind: 'withInput' } },
  moveAllocationToDay: { name: 'MoveAllocationToDay', shape: { kind: 'withInput' } },
  getDailyInspections: { name: 'GetDailyInspections', shape: { kind: 'withInput' } },
  adjustInspectionAllocation: { name: 'AdjustInspectionAllocation', shape: { kind: 'withInput' } },
  removeInspectionAllocation: { name: 'RemoveInspectionAllocation', shape: { kind: 'withInput' } },
  confirmInspection: { name: 'ConfirmInspection', shape: { kind: 'withInput' } },
  registerInspectionAbsence: { name: 'RegisterInspectionAbsence', shape: { kind: 'withInput' } },
  reopenInspection: { name: 'ReopenInspection', shape: { kind: 'withInput' } },
  getPlanningChangeHistory: { name: 'GetPlanningChangeHistory', shape: { kind: 'withInput' } },
  openInspection: { name: 'OpenInspection', shape: { kind: 'withInput' } },
  recordDayMeasurement: { name: 'RecordDayMeasurement', shape: { kind: 'withInput' } },
  // Fase 30 — a medição da equipe: o dia por equipe e a gravação, com a checklist junto.
  getDayTeamMeasurements: { name: 'GetDayTeamMeasurements', shape: { kind: 'withInput' } },
  recordTeamMeasurement: { name: 'RecordTeamMeasurement', shape: { kind: 'withInput' } },
  recordReportedTeamWork: { name: 'RecordReportedTeamWork', shape: { kind: 'withInput' } },
  // Fase 30, etapa 3 — alocar a equipe: a alocação de cada membro, com a marca da equipe.
  createTeamAllocation: { name: 'CreateTeamAllocation', shape: { kind: 'withInput' } },

  // ── Anexos (Fase 22) ──────────────────────────────────────────────────────
  //
  // Os três `attach` levam bytes e por isso usam `withBytes`: o `Uint8Array` cru não
  // sobrevive ao `JSON.stringify`, e a base64 é o mesmo acordo das planilhas.
  attachInspectionFile: { name: 'AttachInspectionFile', shape: { kind: 'withBytes' } },
  attachDailyReportPhoto: { name: 'AttachDailyReportPhoto', shape: { kind: 'withBytes' } },
  attachNonConformityPhoto: { name: 'AttachNonConformityPhoto', shape: { kind: 'withBytes' } },
  attachTeamMeasurementPhoto: {
    name: 'AttachTeamMeasurementPhoto',
    shape: { kind: 'withBytes' },
  },
  listInspectionAttachments: {
    name: 'ListInspectionAttachments',
    shape: { kind: 'withInput' },
  },
  listDailyReportPhotos: { name: 'ListDailyReportPhotos', shape: { kind: 'withInput' } },
  listNonConformityPhotos: { name: 'ListNonConformityPhotos', shape: { kind: 'withInput' } },
  listTeamMeasurementPhotos: { name: 'ListTeamMeasurementPhotos', shape: { kind: 'withInput' } },
  removeInspectionAttachment: {
    name: 'RemoveInspectionAttachment',
    shape: { kind: 'withInput' },
  },
  getAttachmentUrl: { name: 'GetAttachmentUrl', shape: { kind: 'withInput' } },
  // Fase 20 — o trabalho lançado pelo RDO vira alocação e conferência (ADR-131).
  recordReportedWork: { name: 'RecordReportedWork', shape: { kind: 'withInput' } },
  getDailyReport: { name: 'GetDailyReport', shape: { kind: 'withInput' } },
  getOrOpenDailyReport: { name: 'GetOrOpenDailyReport', shape: { kind: 'withInput' } },
  closeDailyReport: { name: 'CloseDailyReport', shape: { kind: 'withInput' } },
  reviseDailyReport: { name: 'ReviseDailyReport', shape: { kind: 'withInput' } },
  setDailyReportWeather: { name: 'SetDailyReportWeather', shape: { kind: 'withInput' } },
  getDailyReportTasks: { name: 'GetDailyReportTasks', shape: { kind: 'withInput' } },
  setDailyReportTasks: { name: 'SetDailyReportTasks', shape: { kind: 'withInput' } },
  setDailyReportNotes: { name: 'SetDailyReportNotes', shape: { kind: 'withInput' } },
  getDailyReportCrew: { name: 'GetDailyReportCrew', shape: { kind: 'withInput' } },
  setDailyReportCrew: { name: 'SetDailyReportCrew', shape: { kind: 'withInput' } },
  setDailyReportEquipment: { name: 'SetDailyReportEquipment', shape: { kind: 'withInput' } },
  copyPreviousWeek: { name: 'CopyPreviousWeek', shape: { kind: 'withInput' } },
  runIntegrityCheck: { name: 'RunIntegrityCheck', shape: { kind: 'withSessionFirst' } },
  repairIntegrity: {
    name: 'RepairIntegrity',
    shape: { kind: 'withSessionFirst', extra: 'findings' },
  },
  getSupplyDigest: { name: 'GetSupplyDigest', shape: { kind: 'withInput' } },
  acknowledgeSupplyMaterial: { name: 'AcknowledgeSupplyMaterial', shape: { kind: 'withInput' } },
  listProjectRequisitions: {
    name: 'ListProjectRequisitions',
    shape: { kind: 'withField', name: 'projectId' },
  },
  signInWithPassword: { name: 'SignInWithPassword', shape: { kind: 'withInputOnly' } },
  setPassword: { name: 'SetPassword', shape: { kind: 'withInput' } },
  signOut: { name: 'SignOut', shape: { kind: 'withInput' } },
  revokeUserSessions: { name: 'RevokeUserSessions', shape: { kind: 'withInput' } },
  // As chaves de API (07/10/2026): a credencial do agente, emitida por quem administra.
  createApiKey: { name: 'CreateApiKey', shape: { kind: 'withInput' } },
  listApiKeys: { name: 'ListApiKeys', shape: { kind: 'withSessionFirst' } },
  revokeApiKey: { name: 'RevokeApiKey', shape: { kind: 'withInput' } },
} as const satisfies Readonly<Record<string, RemoteEndpoint>>;

export type RemoteEndpointKey = keyof typeof REMOTE_ENDPOINTS;
